/** PaymentAccountEnrollmentWizard — ONE provider-neutral wizard, launched from activation or Settings. */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { btnGhost, btnPrimary } from '../finance/financeUi';
import { AccountStatus } from './AccountStatus';
import { AuthorizationStep, BankStep, FieldsStep, IntentStep, STEP_FIELDS } from './WizardSteps';
import { errorDetail, getPath, paApi, setPath } from './paymentAccount';

const STEPS = [['business', 'Business, tax & address'], ['owner', 'Owner / principal'], ['bank', 'Funding bank'],
  ['intent', 'Expected activity'], ['auth', 'Authorizations'], ['review', 'Review & submit']];
const SECRET = ['business.tax_id', 'principal.dob', 'principal.ssn_last4'];

function stepPayload(step, app) {
  const out = {};
  (STEP_FIELDS[step] || []).forEach(([path]) => { const v = getPath(app, path); if (v !== undefined && v !== '') Object.assign(out, setPath(out, path, v)); });
  return out;
}

export function PaymentAccountEnrollmentWizard({ entry = 'settings', onChanged }) {
  const [ov, setOv] = useState(null);
  const [meta, setMeta] = useState(null);
  const [app, setApp] = useState({});
  const [bank, setBank] = useState({ source: 'NEW' });
  const [intents, setIntents] = useState([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    const [o, m] = await Promise.all([paApi.me(), paApi.prefill().catch(() => null)]);
    setOv(o); setMeta(m);
    const acc = o.account;
    setApp(JSON.parse(JSON.stringify(acc?.application || m?.application || {})));
    setIntents(acc?.requested_intents || []);
    const verified = (m?.bank_options || []).find((b) => b.verification_status === 'VERIFIED');
    setBank(acc?.bank_source?.type === 'PAYOUT_PROFILE' ? { source: 'PAYOUT_PROFILE', payment_profile_id: acc.bank_source.payment_profile_id }
      : verified && !acc?.bank_source ? { source: 'PAYOUT_PROFILE', payment_profile_id: verified.payment_profile_id } : { source: 'NEW' });
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errorDetail(e))); }, [load]);

  if (!ov) return null;
  if (!ov.program) return <p className="text-sm text-muted-foreground" data-testid="pa-no-program">MSC hasn't enabled a payment provider program yet. You can still receive payouts and settle externally.</p>;
  if (!ov.eligible) return <p className="text-sm text-muted-foreground" data-testid="pa-not-eligible">Payment Accounts are available to ISOs, Agents and Subagents.</p>;
  const acc = ov.account;
  const done = (fresh) => { setOv((o) => ({ ...o, account: fresh })); onChanged?.(fresh); };
  if (acc && !['DRAFT', 'FAILED'].includes(acc.canonical_status) && !editing) {
    return <AccountStatus account={acc} program={ov.program} onChange={(fresh, retry) => (retry ? setEditing(true) : fresh && done(fresh))} />;
  }
  const onFile = acc?.sensitive_on_file || {};
  if (meta?.tax_id_on_file) onFile['business.tax_id'] = true;
  const key = STEPS[step][0];
  const save = async () => {
    const body = { application: stepPayload(key, app) };
    if (key === 'bank') body.bank = bank;
    if (key === 'intent') body.requested_intents = intents;
    if (['business', 'owner', 'bank', 'intent'].includes(key)) done(await paApi.draft(body));
  };
  const next = async () => { setBusy(true); try { await save(); setStep(step + 1); } catch (e) { toast.error(errorDetail(e)); } setBusy(false); };
  const accept = async (type) => { try { done(await paApi.authorize(type)); } catch (e) { toast.error(errorDetail(e)); } };
  const submit = async () => { setBusy(true); try { done(await paApi.submit()); setEditing(false); toast.success('Payment Account submitted'); } catch (e) { toast.error(errorDetail(e)); } setBusy(false); };
  const skip = async () => { try { await paApi.skip(); toast.success('Skipped — you can set this up later in Settings'); onChanged?.(null); } catch (e) { toast.error(errorDetail(e)); } };

  return (
    <div className="space-y-5" data-testid={`pa-wizard-${entry}`}>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-medium" data-testid="pa-program-provider">{ov.program.provider_name}</span>
        <span className="text-muted-foreground">sponsored by {ov.program.sponsor_name}</span>
        {ov.requirement === 'REQUIRED' && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900" data-testid="pa-required-badge">Required for your profile</span>}
      </div>
      <ol className="flex flex-wrap gap-1.5 text-xs" data-testid="pa-steps">
        {STEPS.map(([k, label], i) => <li key={k} className={`rounded-full px-2.5 py-1 ${i === step ? 'bg-primary text-primary-foreground' : i < step ? 'bg-emerald-100 text-emerald-800' : 'bg-muted'}`}>{i + 1}. {label}</li>)}
      </ol>
      {acc?.validation_errors?.length > 0 && <div className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="pa-validation-errors">{acc.validation_errors.map((e) => <p key={`${e.field}-${e.message}`}>{e.field}: {e.message}</p>)}</div>}
      {(key === 'business' || key === 'owner') && <FieldsStep fields={STEP_FIELDS[key]} app={app} meta={meta || {}} onFile={onFile} onSet={(p, v) => setApp((a) => setPath(a, p, v))} />}
      {key === 'bank' && <BankStep bank={bank} setBank={setBank} options={meta?.bank_options} />}
      {key === 'intent' && <IntentStep intents={intents} setIntents={setIntents} />}
      {key === 'auth' && <AuthorizationStep texts={meta?.authorization_text} accepted={acc?.authorizations || {}} onAccept={accept} />}
      {key === 'review' && (
        <div className="space-y-1 text-sm" data-testid="pa-review">
          <p><b>{getPath(app, 'business.legal_name') || '—'}</b> · {getPath(app, 'business.dba_name') || '—'}</p>
          <p>Owner: {getPath(app, 'principal.first_name')} {getPath(app, 'principal.last_name')} · {getPath(app, 'principal.ownership_percent')}%</p>
          <p>Bank: ••{acc?.bank_source?.last4 || '—'} · Requested: {(acc?.requested_capabilities || []).join(', ').replace(/_/g, ' ') || '—'}</p>
          <p className="text-xs text-muted-foreground">Sensitive details on file: {SECRET.filter((f) => onFile[f]).length}/{SECRET.length}. Submitting creates a submerchant account under {ov.program.sponsor_name}. No money moves.</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {step > 0 && <button type="button" className={btnGhost} onClick={() => setStep(step - 1)} data-testid="pa-back-btn">Back</button>}
        {key !== 'review' ? <button type="button" className={btnPrimary} disabled={busy} onClick={next} data-testid="pa-next-btn">Save & continue</button>
          : <button type="button" className={btnPrimary} disabled={busy} onClick={submit} data-testid="pa-submit-btn">Submit Payment Account</button>}
        {entry === 'activation' && ov.requirement === 'OPTIONAL' && <button type="button" className={btnGhost} onClick={skip} data-testid="pa-skip-btn">Skip for now</button>}
      </div>
    </div>
  );
}
